import { type FC, useMemo } from 'react';
import { Flex, Tag } from 'antd';
import { RedoOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { createRangePresets } from 'components/molecules/oneplaform/timeframe-filter/constants';
import { DateFormats } from 'infrastructure/enums';

import { useFilterLabel } from './useFilterLabel';
import styles from './styles.module.scss';

import type { TOverviewDashboardFilter } from 'infrastructure/hooks/rpm/overview-dashboard';

interface ActiveFiltersBarProps {
  filter: TOverviewDashboardFilter;
  resetFilter: () => void;
  onRemoveFilter: (key: keyof TOverviewDashboardFilter) => void;
  patientName?: string | null;
  clinicName?: string | null;
  agencyDoctorName?: string | null;
  hasAppliedTimeframe?: boolean;
  timeframePresetKey?: string | null;
  filterOrder?: string[];
}

const ActiveFiltersBar: FC<ActiveFiltersBarProps> = ({
  filter,
  resetFilter,
  onRemoveFilter,
  patientName,
  clinicName,
  agencyDoctorName,
  hasAppliedTimeframe = false,
  timeframePresetKey,
  filterOrder = [],
}) => {
  const { t } = useTranslation();
  const { getFilterLabel } = useFilterLabel({
    patientName,
    clinicName,
    agencyDoctorName,
  });

  const timeframeLabel = useMemo(() => {
    if (!filter.startTime || !filter.endTime) return null;

    if (timeframePresetKey) {
      const presets = createRangePresets();
      const preset = presets.find((p) => p.key === timeframePresetKey);
      if (preset) {
        return `${t('labels.timeframe', 'Timeframe')}: ${preset.label}`;
      }
    }

    const startDate = format(
      new Date(filter.startTime),
      DateFormats['MM/dd/yyyy'],
    );
    const endDate = format(new Date(filter.endTime), DateFormats['MM/dd/yyyy']);
    return `${t('labels.timeframe', 'Timeframe')}: ${startDate} - ${endDate}`;
  }, [filter.startTime, filter.endTime, timeframePresetKey, t]);

  const activeFilters = Object.entries(filter)
    .filter(([key, value]) => {
      if (
        key === 'page' ||
        key === 'sorting' ||
        key === 'assignee' ||
        key === 'parentClinicGuid' ||
        key === 'pmpmStatus' ||
        value === undefined ||
        value === null
      ) {
        return false;
      }
      if (key === 'reviewed' && value === false) {
        return false;
      }
      if (key === 'startTime' || key === 'endTime') {
        return false;
      }
      return true;
    })
    .reduce<Map<keyof TOverviewDashboardFilter, string>>(
      (acc, [key, value]) => {
        const label = getFilterLabel(
          key as keyof TOverviewDashboardFilter,
          value,
        );
        if (label !== null) {
          acc.set(key as keyof TOverviewDashboardFilter, label);
        }
        return acc;
      },
      new Map(),
    );

  const hasTimeframe =
    hasAppliedTimeframe && filter.startTime && filter.endTime;
  const totalFilters = activeFilters.size + (hasTimeframe ? 1 : 0);

  if (totalFilters === 0) {
    return null;
  }

  const allFiltersForDisplay: Array<{
    type: 'timeframe' | 'filter';
    key?: keyof TOverviewDashboardFilter;
    label: string;
  }> = [];

  const reversedOrder = [...filterOrder].reverse();

  reversedOrder.forEach((orderKey) => {
    if (orderKey === 'timeframe' && hasTimeframe && timeframeLabel) {
      allFiltersForDisplay.push({
        type: 'timeframe',
        label: timeframeLabel,
      });
    } else {
      const filterKey = orderKey as keyof TOverviewDashboardFilter;
      const label = activeFilters.get(filterKey);
      if (label) {
        allFiltersForDisplay.push({
          type: 'filter',
          key: filterKey,
          label,
        });
      }
    }
  });

  if (hasTimeframe && timeframeLabel && !filterOrder.includes('timeframe')) {
    allFiltersForDisplay.push({
      type: 'timeframe',
      label: timeframeLabel,
    });
  }

  activeFilters.forEach((label, key) => {
    if (!filterOrder.includes(key as string)) {
      allFiltersForDisplay.push({
        type: 'filter',
        key,
        label,
      });
    }
  });

  return (
    <div className={styles.container}>
      <Flex align="center" gap={12} wrap="wrap">
        <span className={styles.label}>
          {t('labels.activeFilters', 'Active filters:')}
        </span>
        {allFiltersForDisplay.map((filterItem) => (
          <Tag
            key={filterItem.type === 'timeframe' ? 'timeframe' : filterItem.key}
            closable
            onClose={(e) => {
              e.preventDefault();
              if (filterItem.type === 'timeframe') {
                onRemoveFilter('startTime');
                onRemoveFilter('endTime');
              } else if (filterItem.key) {
                onRemoveFilter(filterItem.key);
              }
            }}
            className={styles.tag}
          >
            {filterItem.label}
          </Tag>
        ))}
        <button
          type="button"
          onClick={resetFilter}
          className={styles['clear-button']}
        >
          <RedoOutlined className={styles['clear-icon']} />
          {t('controls.clearFilter', 'Clear filter')}
        </button>
      </Flex>
    </div>
  );
};

export default ActiveFiltersBar;
